import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PropTypes from "prop-types";
import DarkModeToggle from "./DarkModeToggle";
import {
  Menu,
  X,
  ShoppingCart,
  User,
  ChevronDown,
  Plus,
  Minus,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
  SheetClose,
} from "./ui/sheet";
import { Button } from "./ui/button";

const CartItem = ({ item, onUpdateQuantity, onRemove }) => (
  <div className="flex items-center py-4">
    <img
      src={item.image || "/api/placeholder/80/80"}
      alt={item.name}
      className="h-20 w-20 rounded object-cover"
    />
    <div className="ml-4 flex-1">
      <h3 className="font-medium text-blue-900 dark:text-blue-100">
        {item.name}
      </h3>
      {(item.selectedSize || item.selectedColor) && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {item.selectedSize && `Size: ${item.selectedSize}`}
          {item.selectedSize && item.selectedColor && " | "}
          {item.selectedColor && `Color: ${item.selectedColor}`}
        </p>
      )}
      <p className="text-sm text-blue-600 dark:text-blue-400">
        ${item.price.toFixed(2)}
      </p>
      <div className="mt-2 flex items-center">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 dark:border-gray-600 dark:text-gray-200"
          onClick={() => onUpdateQuantity(item.cartItemId ?? item.id, item.quantity - 1)}
          disabled={item.quantity <= 1}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <span className="mx-3 dark:text-gray-200">{item.quantity}</span>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 dark:border-gray-600 dark:text-gray-200"
          onClick={() => onUpdateQuantity(item.cartItemId ?? item.id, item.quantity + 1)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
    <Button
      variant="ghost"
      size="icon"
      className="ml-4 dark:text-gray-200 dark:hover:text-white"
      onClick={() => onRemove(item.cartItemId ?? item.id)}
    >
      <X className="h-4 w-4" />
    </Button>
  </div>
);

CartItem.propTypes = {
  item: PropTypes.shape({
    cartItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
    image: PropTypes.string,
    selectedSize: PropTypes.string,
    selectedColor: PropTypes.string,
  }).isRequired,
  onUpdateQuantity: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default function Navigation({
  cartItems = [],
  handleUpdateQuantity,
  handleRemoveItem,
}) {
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup the event listener when component unmounts or dropdown closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsDropdownOpen(false);
    window.location.reload(); // Refresh the page after logout
  };

  const calculateTotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0,
    );
  };

  const handleCheckout = () => {
    navigate("/checkout");
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link
                to="/"
                className="flex items-center text-2xl font-bold text-gray-800 dark:text-white"
              >
                <img
                  src="/ryscircus-logo.svg"
                  alt="Ry's Circus Logo"
                  className="h-12 w-auto mr-2"
                />
                <span className="hidden sm:inline font-handwritten">
                  Ry&apos;s Circus
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex md:items-center md:space-x-8">
              {/* Main Navigation Items */}
              <Link
                to="/#featured"
                className="text-base md:text-lg text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-handwritten"
              >
                The Ryldo
              </Link>
              <Link
                to="/shop"
                className="text-base md:text-lg text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-handwritten"
              >
                Shop
              </Link>

              {/* Dark Mode Toggle */}
              <DarkModeToggle />

              {/* Shopping Cart */}
              <Sheet>
                <SheetTrigger asChild>
                  <button className="relative text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white">
                    <ShoppingCart className="w-6 h-6" />
                    {cartItems.length > 0 && (
                      <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {cartItems.reduce(
                          (total, item) => total + item.quantity,
                          0,
                        )}
                      </span>
                    )}
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="bg-white dark:bg-gray-800 w-full max-w-md dark:text-gray-100"
                >
                  <SheetHeader>
                    <SheetTitle className="text-gray-900 dark:text-white font-handwritten text-xl">
                      Shopping Cart
                    </SheetTitle>
                    <SheetDescription className="text-gray-600 dark:text-gray-300 font-handwritten">
                      Review and manage your selected items
                    </SheetDescription>
                  </SheetHeader>
                  <div className="mt-8">
                    {cartItems.length === 0 ? (
                      <p className="text-gray-600 dark:text-gray-400">
                        Your cart is empty
                      </p>
                    ) : (
                      <>
                        <div className="space-y-4">
                          {cartItems.map((item) => (
                            <CartItem
                              key={String(item.cartItemId ?? item.id)} // Ensure key is a string
                              item={item}
                              onUpdateQuantity={handleUpdateQuantity}
                              onRemove={handleRemoveItem}
                            />
                          ))}
                        </div>
                        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                          <div className="flex justify-between text-lg font-semibold">
                            <span className="text-gray-900 dark:text-white">
                              Total
                            </span>
                            <span className="text-blue-600 dark:text-blue-400">
                              ${calculateTotal().toFixed(2)}
                            </span>
                          </div>
                          <SheetClose asChild className="w-full">
                            <Button
                              className="w-full mt-4 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-handwritten"
                              onClick={handleCheckout}
                              disabled={false}
                            >
                              Checkout
                            </Button>
                          </SheetClose>
                        </div>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>

              {/* User Menu Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white focus:outline-none"
                >
                  <User className="w-6 h-6 mr-1" />
                  <ChevronDown className="w-4 h-4" />
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-700 rounded-md shadow-lg py-1 z-10">
                    {user ? (
                      <>
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          Profile
                        </Link>
                        <Link
                          to="/order-history"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          Order History
                        </Link>
                        {user.isAdmin && (
                          <Link
                            to="/admin"
                            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Admin Dashboard
                          </Link>
                        )}
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                        >
                          Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          Login
                        </Link>
                        <Link
                          to="/register"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          Sign Up
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button and Cart */}
            <div className="md:hidden flex items-center space-x-4">
              {/* Dark Mode Toggle for Mobile */}
              <DarkModeToggle />
              
              {/* Shopping Cart for Mobile */}
              <Sheet>
                <SheetTrigger asChild>
                  <button className="relative text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white">
                    <ShoppingCart className="w-6 h-6" />
                    {cartItems.length > 0 && (
                      <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {cartItems.reduce(
                          (total, item) => total + item.quantity,
                          0,
                        )}
                      </span>
                    )}
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="bg-white dark:bg-gray-800 w-full max-w-md dark:text-gray-100"
                >
                  <SheetHeader>
                    <SheetTitle className="text-gray-900 dark:text-white font-handwritten text-xl">
                      Shopping Cart
                    </SheetTitle>
                    <SheetDescription className="text-gray-600 dark:text-gray-300 font-handwritten">
                      Review and manage your selected items
                    </SheetDescription>
                  </SheetHeader>
                  <div className="mt-8">
                    {cartItems.length === 0 ? (
                      <p className="text-gray-600 dark:text-gray-400">
                        Your cart is empty
                      </p>
                    ) : (
                      <>
                        <div className="space-y-4">
                          {cartItems.map((item) => (
                            <CartItem
                              key={String(item.cartItemId ?? item.id)} // Ensure key is a string
                              item={item}
                              onUpdateQuantity={handleUpdateQuantity}
                              onRemove={handleRemoveItem}
                            />
                          ))}
                        </div>
                        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                          <div className="flex justify-between text-lg font-semibold">
                            <span className="text-gray-900 dark:text-white">
                              Total
                            </span>
                            <span className="text-blue-600 dark:text-blue-400">
                              ${calculateTotal().toFixed(2)}
                            </span>
                          </div>
                          <SheetClose asChild className="w-full">
                            <Button
                              className="w-full mt-4 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-handwritten"
                              onClick={handleCheckout}
                              disabled={false}
                            >
                              Checkout
                            </Button>
                          </SheetClose>
                        </div>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white p-2"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 bg-white dark:bg-gray-800">
                <Link
                  to="/#featured"
                  className="block px-3 py-2 text-base text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-handwritten"
                  onClick={() => setIsMenuOpen(false)}
                >
                  The Ryldo
                </Link>
                <Link
                  to="/shop"
                  className="block px-3 py-2 text-base text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-handwritten"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Shop
                </Link>

                {user ? (
                  <>
                    {user.isAdmin && (
                      <Link
                        to="/admin"
                        className="block px-3 py-2 text-base text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-handwritten"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      className="block px-3 py-2 text-base text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-handwritten"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/order-history"
                      className="block px-3 py-2 text-base text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-handwritten"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Order History
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="block w-full text-left px-3 py-2 text-base text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-handwritten"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block px-3 py-2 text-base text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-handwritten"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="block px-3 py-2 text-base text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-handwritten"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

Navigation.propTypes = {
  cartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      quantity: PropTypes.number.isRequired,
      image: PropTypes.string,
      selectedSize: PropTypes.string,
      selectedColor: PropTypes.string,
    }),
  ),
  handleUpdateQuantity: PropTypes.func.isRequired,
  handleRemoveItem: PropTypes.func.isRequired,
};
